/**
 * #721 — Care events: the digital meal / medication administration log.
 *
 * Kylie (HTHD receptionist, ex-Dogtopia, 2026-07-29 walkthrough):
 *   "If they didn't eat breakfast or lunch or dinner, we would log that, and
 *    it would keep track of how many nights they didn't."
 *
 * This file is the FUNCTIONAL CORE: closed value sets, the Zod write
 * contract, the error union, and `computeSlotStreaks` — a pure function that
 * turns a list of events into the "refused dinner 2 nights running" read.
 * It touches no database and no Express. The imperative shell is
 * `prisma-adapter.ts` (raw SQL, explicit tenant predicates) and
 * `routes-admin.ts` / `routes-customer.ts`.
 *
 * APPEND-ONLY BY CONTRACT — there is no update and no delete anywhere in
 * this module. A correction is a new event; the later `occurredAt` wins for
 * its slot. That is what makes the log a PACFA-grade administration record
 * (#553) instead of a mutable status field: "we thought he ate, then found
 * the bowl full" is two rows, and both survive.
 */
import { z } from 'zod';

// ──────────────────────────────────────────────────────────────────────
// Closed value sets — the ONE source of truth. The DB CHECK constraints in
// 20260807150000_add_care_events (and, for the medication outcomes,
// 20261025100802_sawyer_a2_care_event_med_outcomes) mirror these exactly; if
// you change one, change both, and add a migration.
// ──────────────────────────────────────────────────────────────────────

export const CARE_EVENT_KINDS = ['meal', 'medication'] as const;
export type CareEventKind = (typeof CARE_EVENT_KINDS)[number];

export const MEAL_SLOTS = ['breakfast', 'lunch', 'dinner', 'snack'] as const;
export type MealSlot = (typeof MEAL_SLOTS)[number];

export const MEDICATION_SLOTS = [
  'am',
  'midday',
  'pm',
  'bedtime',
  'as_needed',
] as const;
export type MedicationSlot = (typeof MEDICATION_SLOTS)[number];

/**
 * Meal outcomes as STORED. `logged` is read-only legacy: it is what the
 * backfill wrote for pre-#721 `communication_logs` feeding rows, which
 * recorded that a meal was logged and had nowhere to record whether the dog
 * ate it. Treating those as `ate_all` would fabricate a health signal, so
 * they carry their own value and the streak read treats them as UNKNOWN.
 */
export const MEAL_OUTCOMES = [
  'ate_all',
  'ate_partial',
  'refused',
  'logged',
] as const;
export type MealOutcome = (typeof MEAL_OUTCOMES)[number];

/**
 * What the API will ACCEPT on a write. `logged` is deliberately absent: no
 * new event may claim "outcome unknown" — recording the outcome is the
 * entire feature. The DB CHECK is wider than this on purpose (it must still
 * admit the backfilled history).
 */
export const WRITABLE_MEAL_OUTCOMES = [
  'ate_all',
  'ate_partial',
  'refused',
] as const;
export type WritableMealOutcome = (typeof WRITABLE_MEAL_OUTCOMES)[number];

/**
 * Medication outcomes as STORED (the DB CHECK's set). Sawyer 08 meds-feeding (A2)
 * added 'prepared' (the AM crew set the dose out) and
 * 'partially_administered' (some of it went down).
 */
export const MEDICATION_OUTCOMES = [
  'given',
  'refused',
  'missed',
  'prepared',
  'partially_administered',
] as const;
export type MedicationOutcome = (typeof MEDICATION_OUTCOMES)[number];

/**
 * What the per-dog care log (POST /dogs/:dogId/care-events) accepts — the
 * #721 set, unchanged on purpose (refuter C-10): the med pass is the only
 * writer of the two new statuses, so a "Prepared" always belongs to a named
 * medication and a slot on the day's pass.
 */
export const LOG_MEDICATION_OUTCOMES = ['given', 'refused', 'missed'] as const;

/** What the med pass offers, in the order its buttons read. 'given' reads "Administered". */
export const MED_PASS_OUTCOMES = ['prepared', 'given', 'partially_administered', 'refused'] as const;
export type MedPassOutcome = (typeof MED_PASS_OUTCOMES)[number];

/** Outcomes that count as "the dog did not eat" for the streak read. */
export const REFUSAL_MEAL_OUTCOMES: ReadonlySet<string> = new Set(['refused']);

// ──────────────────────────────────────────────────────────────────────
// Domain record
// ──────────────────────────────────────────────────────────────────────

export interface CareEventRecord {
  id: string;
  tenantId: string;
  dogId: string;
  bookingId: string | null;
  staffUserId: string | null;
  kind: CareEventKind;
  slot: string;
  outcome: string;
  medicationId: string | null;
  notes: string | null;
  parentVisible: boolean;
  /** ISO-8601 UTC instant the care actually happened. */
  occurredAt: string;
  createdAt: string;
  /** Denormalised for the staff surfaces ("Kylie · 6:12 PM"). */
  staffFirstName?: string | null;
  staffLastName?: string | null;
}

// ──────────────────────────────────────────────────────────────────────
// Write contract (parse, don't validate — Zod at the boundary)
// ──────────────────────────────────────────────────────────────────────

const isoInstant = z
  .string()
  .datetime({ offset: true, message: 'occurredAt must be an ISO-8601 timestamp.' });

const notes = z
  .string()
  .trim()
  .max(500, 'Keep the note under 500 characters.')
  .optional();

/**
 * A meal outcome. `slot` and `outcome` are cross-checked by the union
 * discriminant, so an impossible pair ("medication / ate_all") is
 * unrepresentable rather than merely rejected downstream — the illegal
 * state cannot be constructed.
 */
const mealEventSchema = z.object({
  kind: z.literal('meal'),
  slot: z.enum(MEAL_SLOTS, {
    message: `Meal must be one of: ${MEAL_SLOTS.join(', ')}.`,
  }),
  outcome: z.enum(WRITABLE_MEAL_OUTCOMES, {
    message: `Outcome must be one of: ${WRITABLE_MEAL_OUTCOMES.join(', ')}.`,
  }),
  notes,
  bookingId: z.string().uuid('Invalid booking id.').optional(),
  occurredAt: isoInstant.optional(),
  /**
   * Per-event half of the parent-visibility gate. Default FALSE — a staffer
   * shares an outcome deliberately, never by omission. The tenant half
   * (TenantConfig.careLogParentVisible) must ALSO be on.
   */
  parentVisible: z.boolean().default(false),
});

const medicationEventSchema = z.object({
  kind: z.literal('medication'),
  slot: z.enum(MEDICATION_SLOTS, {
    message: `Time must be one of: ${MEDICATION_SLOTS.join(', ')}.`,
  }),
  outcome: z.enum(LOG_MEDICATION_OUTCOMES, {
    message: `Outcome must be one of: ${LOG_MEDICATION_OUTCOMES.join(', ')}.`,
  }),
  notes,
  bookingId: z.string().uuid('Invalid booking id.').optional(),
  medicationId: z.string().uuid('Invalid medication id.').optional(),
  occurredAt: isoInstant.optional(),
  /**
   * Accepted so the client contract is uniform, but INERT in v1: the
   * customer serializer drops every medication event unconditionally, so a
   * `true` here changes nothing an owner can see. Med-outcome copy is
   * pending liability review (#553). The write path stores what the staffer
   * chose rather than silently rewriting it — when the review lands, the
   * intent is already on the row.
   */
  parentVisible: z.boolean().default(false),
});

export const createCareEventSchema = z.discriminatedUnion('kind', [
  mealEventSchema,
  medicationEventSchema,
]);
export type CreateCareEventInput = z.infer<typeof createCareEventSchema>;

/**
 * What the STORE may write — wider than `CreateCareEventInput` in exactly
 * ONE place, and this type is the whole seam.
 *
 * The legacy kennel `POST /api/v2/admin/kennel/feeding` endpoint (which #721
 * migrates off `communication_logs`) still accepts a call with no outcome,
 * because an admin-app build that predates this release sends none — and
 * frontends auto-deploy on merge while the server deploys by hand, so that
 * pairing really happens. Such a call writes `'logged'` ("served, outcome
 * not recorded"), never `'ate_all'`: an un-observed meal must not become a
 * health signal, and `computeSlotStreaks` treats `'logged'` as UNKNOWN.
 *
 * `createCareEventSchema` still refuses `'logged'`, so the care-events API
 * itself cannot produce one. Widening happens here and nowhere else.
 */
export type AppendCareEventInput =
  | (Omit<Extract<CreateCareEventInput, { kind: 'meal' }>, 'outcome'> & {
      outcome: MealOutcome;
    })
  | Extract<CreateCareEventInput, { kind: 'medication' }>;

export const careEventQuerySchema = z.object({
  /** How many days of history to read back. Bounded — this feeds a card. */
  days: z.coerce.number().int().min(1).max(90).default(14),
});
export type CareEventQueryInput = z.infer<typeof careEventQuerySchema>;

// ──────────────────────────────────────────────────────────────────────
// Sawyer 08 meds-feeding (A1, G10) — the day-wide meds & feeding read.
// ──────────────────────────────────────────────────────────────────────

/** A real calendar day, YYYY-MM-DD (2026-13-40 is refused, not rolled over). */
export function isCalendarDay(v: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(`${v}T12:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}

export const CARE_DAY_SERVICES = ['all', 'boarding', 'daycare'] as const;
export type CareDayService = (typeof CARE_DAY_SERVICES)[number];

export const careDayQuerySchema = z.object({
  /** Facility day; absent = the facility's today. */
  date: z
    .string({ message: 'Pick a valid date' })
    .refine(isCalendarDay, { message: 'Pick a valid date' })
    .optional(),
  service: z
    .enum(CARE_DAY_SERVICES, { message: 'Service must be all, boarding or daycare' })
    .default('all'),
  /**
   * The Day list carries its service chip as a service-type id (lane 02's
   * link seam). Resolved server-side, tenant-scoped, to boarding/daycare;
   * anything else reads as "all".
   */
  serviceTypeId: z.string().uuid('Pick a valid service').optional(),
});
export type CareDayQueryInput = z.infer<typeof careDayQuerySchema>;

/**
 * Sawyer 08 meds-feeding (A2) — one tap on the med pass. STRICT: an unknown key is a
 * 400. There is deliberately no `occurredAt` (refuter C-2): the server
 * writes NOW(), so a legal administration record can be neither backdated
 * nor future-dated. A correction is a new tap (append-only).
 */
export const createDoseSchema = z
  .object({
    dogId: z.string().uuid('Pick a dog'),
    medicationId: z.string().uuid('Pick a medication'),
    slot: z.enum(MEDICATION_SLOTS, { message: 'Times must be AM, Noon, PM, Bedtime or As needed' }),
    outcome: z.enum(MED_PASS_OUTCOMES, {
      message: 'Choose Prepared, Administered, Partly given or Refused',
    }),
    bookingId: z.string().uuid('Pick a visit').optional(),
    notes: z.string().trim().max(500, 'Keep the note under 500 characters.').optional(),
  })
  .strict();
export type CreateDoseInput = z.infer<typeof createDoseSchema>;

// ──────────────────────────────────────────────────────────────────────
// Errors — discriminated union, mapped to HTTP in the route layer.
// ──────────────────────────────────────────────────────────────────────

export type CareEventError =
  /** Caller's StaffUser/Customer has no resolvable tenant → 401/403, fail closed. */
  | { kind: 'tenant_unresolved' }
  /** The resolver query itself failed (Neon cold start) → retryable 503. */
  | { kind: 'tenant_resolver_error'; cause: unknown }
  /**
   * The dog does not exist IN THE CALLER'S TENANT. Cross-tenant reads AND
   * writes both land here → 404 with zero rows written. A 403 would confirm
   * the id exists somewhere; 404 says nothing.
   */
  | { kind: 'dog_not_found' }
  /** A referenced booking/medication is not this dog's → 404, never a write. */
  | { kind: 'reference_not_found' };

// ──────────────────────────────────────────────────────────────────────
// Streak computation — the pure read behind "refused dinner 2 nights
// running". No I/O; every test of it is a unit test.
// ──────────────────────────────────────────────────────────────────────

export interface SlotStreak {
  slot: MealSlot;
  /** Consecutive CALENDAR DAYS, ending on the most recent day with an event for this slot, whose outcome was a refusal. */
  days: number;
  /** Local calendar day (YYYY-MM-DD) of the most recent refusal in the run. */
  lastRefusedOn: string;
}

export interface CareStreakSummary {
  /** Every meal slot currently on a refusal run of ≥1 day, longest first. */
  streaks: SlotStreak[];
  /** The longest run, or null. What the badge renders. */
  worst: SlotStreak | null;
}

/**
 * Calendar day (YYYY-MM-DD) of an instant, in an IANA timezone.
 *
 * Timezone is load-bearing and NOT cosmetic: dinner is logged around 18:00
 * local, which is the NEXT UTC day for America/Denver. Bucketing on the UTC
 * date would report "refused dinner 2 days running" for two consecutive
 * dinners logged 24h apart and, worse, would silently merge a Sunday and
 * Monday dinner into one bucket. `en-CA` gives ISO order (YYYY-MM-DD) from
 * Intl without hand-rolling a formatter.
 */
export function localDayOf(instant: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(instant);
  } catch {
    // An invalid IANA name must not throw into a read path; UTC is the
    // honest fallback and matches how the row is stored.
    return instant.toISOString().slice(0, 10);
  }
}

/** YYYY-MM-DD of the day before `day`. Pure string/date arithmetic. */
function previousDay(day: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Per-slot refusal streaks.
 *
 * Rules, chosen so the badge can never overstate a health signal:
 *   · Only `kind: 'meal'` events count. Medication outcomes have their own
 *     taxonomy and their own (pending) copy review — folding them in would
 *     produce "refused 3 in a row" mixing a skipped pill with a skipped
 *     dinner.
 *   · Events are bucketed by LOCAL calendar day per slot. Within a day the
 *     LATEST `occurredAt` wins — that is the append-only correction rule:
 *     "logged refused at 18:05, re-logged ate_partial at 18:40" reads as
 *     ate_partial, and the refusal row still exists in the history.
 *   · The run walks backwards from the most recent day that HAS an event
 *     for that slot. A day with no event ENDS the run: the dog may have
 *     been home. Claiming continuity across a gap would be inventing data.
 *   · `logged` (legacy backfill, outcome unknown) is not a refusal, so it
 *     ends a run exactly like `ate_all` does. Unknown never counts as bad.
 */
export function computeSlotStreaks(
  events: readonly CareEventRecord[],
  opts: { timeZone: string },
): CareStreakSummary {
  // slot → day → { at, outcome } of the latest event that day.
  const bySlot = new Map<
    MealSlot,
    Map<string, { at: number; outcome: string }>
  >();

  for (const e of events) {
    if (e.kind !== 'meal') continue;
    if (!(MEAL_SLOTS as readonly string[]).includes(e.slot)) continue;
    const slot = e.slot as MealSlot;
    const at = new Date(e.occurredAt).getTime();
    if (!Number.isFinite(at)) continue;
    const day = localDayOf(new Date(at), opts.timeZone);

    let days = bySlot.get(slot);
    if (!days) {
      days = new Map();
      bySlot.set(slot, days);
    }
    const existing = days.get(day);
    // Strictly-later wins; ties keep the first seen so the result does not
    // depend on input ordering.
    if (!existing || at > existing.at) days.set(day, { at, outcome: e.outcome });
  }

  const streaks: SlotStreak[] = [];
  for (const [slot, days] of bySlot) {
    const sortedDays = [...days.keys()].sort();
    const mostRecent = sortedDays[sortedDays.length - 1];
    if (!mostRecent) continue;
    if (!REFUSAL_MEAL_OUTCOMES.has(days.get(mostRecent)!.outcome)) continue;

    let run = 0;
    let cursor = mostRecent;
    for (;;) {
      const entry = days.get(cursor);
      if (!entry || !REFUSAL_MEAL_OUTCOMES.has(entry.outcome)) break;
      run += 1;
      cursor = previousDay(cursor);
    }
    if (run > 0) streaks.push({ slot, days: run, lastRefusedOn: mostRecent });
  }

  // Longest first; ties broken by the most recent refusal, then slot name so
  // the order is total and the UI never reshuffles between identical reads.
  streaks.sort(
    (a, b) =>
      b.days - a.days ||
      b.lastRefusedOn.localeCompare(a.lastRefusedOn) ||
      a.slot.localeCompare(b.slot),
  );

  return { streaks, worst: streaks[0] ?? null };
}

/** "Oct 26" for a YYYY-MM-DD calendar day (no timezone shift: it is already a day). */
function shortDay(day: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/**
 * One-line human copy for a streak. Returns null when there is nothing to
 * say — the caller renders nothing rather than an empty badge.
 *
 * Sawyer #1295 (10-26): the desk's words are "Ate none / Ate some / Ate
 * all", so a refused meal reads "Ate none at dinner", never "Refused".
 *
 * `today` (the FACILITY's calendar day) keeps the copy honest about WHEN:
 * the run ends on the most recent day that has a log for that meal, which
 * is not always today (a boarder who ate nothing at dinner last night and
 * whose dinner tonight is not logged yet). Without `today` the copy names
 * no day at all, so it can never claim "today" for yesterday's meal.
 */
export function describeStreak(
  streak: SlotStreak | null,
  opts: { today?: string } = {},
): string | null {
  if (!streak || streak.days < 1) return null;
  const base =
    streak.days === 1
      ? `Ate none at ${streak.slot}`
      : `Ate none at ${streak.slot} ${streak.days} days running`;
  if (!opts.today || streak.lastRefusedOn === opts.today) return base;
  const when =
    streak.lastRefusedOn === previousDay(opts.today) ? 'yesterday' : shortDay(streak.lastRefusedOn);
  if (streak.days === 1) return when === 'yesterday' ? `${base} yesterday` : `${base} on ${when}`;
  return `${base}, through ${when}`;
}

// ──────────────────────────────────────────────────────────────────────
// Sawyer #1295 (10-29) — "Ate none" the FIRST time, not at end of day.
//
// Sawyer: "a warning that a dog has 'ate none' … So, everyone knows that
// dog specifically needs to be attended to." On 10-29 he chose WHEN: the
// first meal logged "Ate none" raises it. This is the one rule every desk
// surface reads (Front Desk, the dashboard's arrivals/departures, the Day
// list, the meds & feeding list, the kennel roster, the pet card and the
// care log), computed on the server and shipped ready-made.
// ──────────────────────────────────────────────────────────────────────

export interface AteNoneWarning {
  /** Meals whose latest log on the day is "Ate none", in day order (breakfast first). */
  meals: MealSlot[];
  /** Ready-made copy: "Ate none at breakfast", "Ate none: breakfast, lunch". */
  label: string;
  /**
   * The longest consecutive-day run among those meals (≥1). Drives the badge's
   * severity exactly as a streak does (3+ days is the vet conversation).
   */
  days: number;
}

/**
 * The "Ate none" warning for ONE facility day, or null.
 *
 * Rules (each one a unit test):
 *   · One entry per MEAL, never per log: re-logging breakfast "Ate none"
 *     twice is still one breakfast.
 *   · The latest log for a meal on the day wins (the append-only correction
 *     rule `computeSlotStreaks` already uses): breakfast "Ate none" then
 *     "Ate some" clears breakfast.
 *   · Days are the facility's calendar days in `timeZone`, never UTC: a
 *     dinner logged at 19:00 in Denver is already tomorrow in UTC.
 *   · Only meals logged ON `day` count. Yesterday's refusal is not today's
 *     warning (it stays visible as a streak where streaks show).
 *   · Logs after `day` are ignored, so a past day reads as it stood.
 */
export function computeAteNoneOn(
  events: readonly CareEventRecord[],
  opts: { timeZone: string; day: string },
): AteNoneWarning | null {
  const upToDay = events.filter((e) => {
    if (e.kind !== 'meal') return false;
    const at = new Date(e.occurredAt).getTime();
    if (!Number.isFinite(at)) return false;
    return localDayOf(new Date(at), opts.timeZone) <= opts.day;
  });
  const order = (s: MealSlot) => (MEAL_SLOTS as readonly string[]).indexOf(s);
  const onDay = computeSlotStreaks(upToDay, { timeZone: opts.timeZone })
    .streaks.filter((s) => s.lastRefusedOn === opts.day)
    .sort((a, b) => order(a.slot) - order(b.slot));
  if (onDay.length === 0) return null;
  const days = Math.max(...onDay.map((s) => s.days));
  const label =
    onDay.length === 1
      ? describeStreak(onDay[0]!)!
      : `Ate none: ${onDay
          .map((s) => (s.days >= 2 ? `${s.slot} (${s.days} days running)` : s.slot))
          .join(', ')}`;
  return { meals: onDay.map((s) => s.slot), label, days };
}

/**
 * `computeAteNoneOn` for a whole roster from ONE `listForDogs` read. Only
 * dogs with a warning are in the map; a caller sends `null` for the rest.
 */
export function ateNoneByDog(
  events: readonly CareEventRecord[],
  opts: { timeZone: string; day: string },
): Map<string, AteNoneWarning> {
  const byDog = new Map<string, CareEventRecord[]>();
  for (const e of events) {
    const list = byDog.get(e.dogId);
    if (list) list.push(e);
    else byDog.set(e.dogId, [e]);
  }
  const out = new Map<string, AteNoneWarning>();
  for (const [dogId, list] of byDog) {
    const w = computeAteNoneOn(list, opts);
    if (w) out.set(dogId, w);
  }
  return out;
}

/**
 * How far back a roster's care read looks — long enough that a meal's run
 * ("3 days running") is counted whole. A run is broken by any day without a
 * log for that meal, so a longer window buys nothing but rows.
 */
export const CARE_STREAK_WINDOW_DAYS = 30;

// ──────────────────────────────────────────────────────────────────────
// Data store seam (dependency-inverted; the adapter is raw SQL)
// ──────────────────────────────────────────────────────────────────────

export interface CareEventDataStore {
  /**
   * Care events for one dog, newest first, within `days` of now. MUST scope
   * by tenant AND dog in the SQL — never post-filter.
   */
  listForDog(args: {
    tenantId: string;
    dogId: string;
    days: number;
  }): Promise<CareEventRecord[]>;
  /**
   * Care events for MANY dogs at once, newest first. Exists so a roster of
   * N dogs costs ONE query instead of N — the kennel floor renders a streak
   * badge per row, and an N+1 there is a slow morning on a tablet.
   * Same tenant gate as `listForDog`; an empty `dogIds` does no query.
   */
  listForDogs(args: {
    tenantId: string;
    dogIds: readonly string[];
    days: number;
  }): Promise<CareEventRecord[]>;
  /**
   * Append one event. Returns null when the dog is not in `tenantId` — the
   * existence check and the tenant gate are the SAME query, so a
   * cross-tenant write cannot race past it.
   */
  append(args: {
    tenantId: string;
    dogId: string;
    staffUserId: string;
    input: AppendCareEventInput;
  }): Promise<CareEventRecord | null>;
  /** Owner id for a dog within a tenant, or null. Used by the customer read. */
  findDogOwner(args: {
    tenantId: string;
    dogId: string;
  }): Promise<string | null>;
  /**
   * Sawyer 08 meds-feeding (A1/A2) — every MEDICATION event for these dogs
   * inside one facility day's instants, with the staff name (tenant-gated
   * join). Same tenant gate as `listForDogs`; empty `dogIds` does no query.
   */
  listMedicationEventsForDay(args: {
    tenantId: string;
    dogIds: readonly string[];
    dayStart: Date;
    dayEnd: Date;
  }): Promise<CareEventRecord[]>;
  /**
   * Sawyer A2 — append one med-pass dose. STRICT where `append` is lenient:
   * the medication must be this dog's, active, and the dog this tenant's;
   * a named booking must be a visit that holds this dog. Any miss writes
   * ZERO rows and says which (the gate is the INSERT ... SELECT itself).
   */
  /**
   * Sawyer 08 meds-feeding (A2, #1033) — the visit a SERVICE LEG hangs off,
   * or null when `bookingId` is not a leg in this tenant. A dose is given
   * on the visit, never on a nail-trim leg; the route answers 409
   * `leg_follows_visit` when this returns an id.
   */
  findLegParent(args: { tenantId: string; bookingId: string }): Promise<string | null>;
  appendDose(args: {
    tenantId: string;
    staffUserId: string;
    input: CreateDoseInput;
  }): Promise<
    | { kind: 'ok'; record: CareEventRecord }
    | { kind: 'medication_not_found' }
    | { kind: 'visit_not_found' }
  >;
}
